import { Button, ErrorMessage, Screen, Text, TextInput } from "components"
import React, { useEffect, useState, useCallback } from "react"
import { StatusBar, TouchableOpacity, View, KeyboardAvoidingView, Platform } from "react-native"
import { style } from "./styles"
import { Controller, useForm } from "react-hook-form"
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field"
import { translate } from "i18n"
import { useNavigation, useRoute } from "@react-navigation/native"
import { colors } from "colors"
import { typography } from "theme"
import DropDownPicker from "react-native-dropdown-picker"
import moment from "moment"
import flash from "config/flash"
import { useColor } from "app/theme/getThemes"
import { DatePickerModal } from "react-native-paper-dates"
import { RadioButtonProps } from "components/RadioButton/props"
import Icon from "@expo/vector-icons/Feather"
import { PhoneNumberInput } from "components/PhoneNumberInput"
import { getAllCountries, ICountry } from "react-native-international-phone-number"
import { observer } from "mobx-react-lite"

export const RegisterScreen = observer(() => {
  const route = useRoute()
  const initialUserData = route.params?.userData
  const { companyTheme } = useColor()
  const navigation = useNavigation<any>()
  const [isLoading, setIsLoading] = useState(false)
  const [idVisible, setIDVisible] = useState(false)
  const [countryCode, setCountryCode] = useState("+60")
  const [initialDefaultCountry, setInitialDefaultCountry] = useState(null)
  const [selectedFullCountryInfo, setSelectedFullCountryInfo] = useState<ICountry | null>(null)
  const { control, handleSubmit, setValue, watch, clearErrors, reset } = useForm({
    reValidateMode: "onSubmit",
  })

  const RadioButton = (props: RadioButtonProps) => {
    const { children, checked, ...restProps } = props
    const { companyTheme } = useColor()

    return (
      <View style={style.radiocontainer}>
        <TouchableOpacity
          style={{
            alignItems: "center",
            borderColor: "#E6E6E6",
            borderRadius: 10,
            borderWidth: 5,
            height: 20,
            justifyContent: "center",
            marginRight: 5,
            width: 20,
          }}
          {...restProps}
        >
          <View
            style={[
              style.radioButtonIcon,
              checked && style.checkedButton,
              {
                backgroundColor: checked && companyTheme?.first,
                width: 20,
                height: 20,
                borderRadius: 999,
              },
            ]}
          />
        </TouchableOpacity>
        {children}
      </View>
    )
  }

  const [isTncChecked, setIsTncChecked] = useState(false)
  const [IDList] = useState([
    { label: translate("common.ic").toUpperCase(), value: "ic" },
    { label: translate("common.passport").toUpperCase(), value: "passport" },
  ])

  const [genderVisible, setGenderVisible] = useState(false)
  const [marriedVisible, setMarriedVisible] = useState(false)
  const [genderList] = useState([
    { label: translate("common.male").toUpperCase(), value: "male" },
    { label: translate("common.female").toUpperCase(), value: "female" },
  ])

  const [raceList] = useState([
    { label: translate("common.malay").toUpperCase(), value: "malay" },
    { label: translate("common.chinese").toUpperCase(), value: "chinese" },
    { label: translate("common.india").toUpperCase(), value: "indian" },
    { label: translate("common.others").toUpperCase(), value: "other" },
  ])

  const [maritalList] = useState([
    { label: translate("common.single").toUpperCase(), value: "single" },
    { label: translate("common.married").toUpperCase(), value: "married" },
  ])

  const pinRef = useBlurOnFulfill({ value: watch("pin"), cellCount: 6 })
  const confirmPinRef = useBlurOnFulfill({ value: watch("confirmPin"), cellCount: 6 })
  const [pinProps, getPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("pin"),
    setValue: (e) => setValue("pin", e),
  })
  const [confirmPinProps, getConfirmPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("confirmPin"),
    setValue: (e) => setValue("confirmPin", e),
  })

  const [existUser, setExistUser] = useState(false)
  const [inactiveUser, setInactiveUser] = useState(false)
  const [userData, setUserData] = useState([])

  const [isDatePickerVisible, setDatePickerVisibility] = useState(false)
  const showDatePicker = () => {
    setDatePickerVisibility(true)
  }

  const hideDatePicker = () => {
    setDatePickerVisibility(false)
  }

  const outletCode = route.params?.outletCode
    ? route.params?.outletCode.split("outletCode=")[1]
    : null

  useEffect(() => {
    const allCountries: ICountry[] = getAllCountries()

    if (initialUserData && initialUserData.contact_number) {
      const fullNumberFromDB = initialUserData.contact_number
      let bestMatch: {
        country: ICountry | null
        nationalNumber: string
        callingCodeValue: string
      } = {
        country: null,
        nationalNumber: fullNumberFromDB,
        callingCodeValue: "",
      }

      const sortedCountries = [...allCountries].sort((a, b) => {
        const lenA = a.callingCode.substring(1).length
        const lenB = b.callingCode.substring(1).length
        return lenB - lenA
      })

      for (const country of sortedCountries) {
        const callingCodeWithoutPlus = country.callingCode.substring(1)
        if (fullNumberFromDB.startsWith(callingCodeWithoutPlus)) {
          bestMatch = {
            country: country,
            nationalNumber: fullNumberFromDB.substring(callingCodeWithoutPlus.length),
            callingCodeValue: country.callingCode,
          }
          break
        }
      }

      setInactiveUser(true)
      reset({
        name: initialUserData.name,
        email: initialUserData?.email,
        phoneNumber: bestMatch.nationalNumber,
        race: initialUserData?.race?.toString(),
        dob: initialUserData?.dob ? new Date(initialUserData.dob) : undefined,
        memberId: initialUserData?.member_id,
        gender: initialUserData?.gender,
        maritalStatus: initialUserData?.marital_status,
      })

      if (bestMatch.country) {
        setInitialDefaultCountry(bestMatch.country.cca2)
        setCountryCode(bestMatch.callingCodeValue)
        setSelectedFullCountryInfo(bestMatch.country)
      } else {
        const fallbackCountry = allCountries.find((c) => c.cca2 === "MY")
        if (fallbackCountry) {
          setInitialDefaultCountry(fallbackCountry.cca2)
          setCountryCode(fallbackCountry.callingCode)
          setSelectedFullCountryInfo(bestMatch.country)
        } else {
          setInitialDefaultCountry(null)
          setCountryCode("")
          setSelectedFullCountryInfo(null)
        }
      }
    } else {
      setInactiveUser(false)
      reset({
        name: "",
        email: "",
        phoneNumber: "",
        race: "",
        dob: undefined,
        memberId: "",
        gender: "",
        maritalStatus: "",
        referral_code_used: "",
      })
      setValue("race", "")
      setValue("gender", "")
      setValue("maritalStatus", "")

      const defaultNewUserCountry = allCountries.find((c) => c.cca2 === "MY")
      if (defaultNewUserCountry) {
        setInitialDefaultCountry(defaultNewUserCountry.cca2)
        setCountryCode(defaultNewUserCountry.callingCode)
        setSelectedFullCountryInfo(defaultNewUserCountry)
      } else {
        setInitialDefaultCountry(null)
        setCountryCode("")
        setSelectedFullCountryInfo(null)
      }
    }
  }, [initialUserData, reset, setValue])

  const handleWhatsAppPress = useCallback(
    async (currentUserData) => {
      const originalPhoneNumber = currentUserData?.contact_number

      console.log(`Sending OTP to ${originalPhoneNumber} on Whatsapp`)
      const requestBody = {
        company_code: process.env.NEXT_PUBLIC_COMPANY_CODE || process.env.COMPANY_CODE,
        contact_number: originalPhoneNumber,
      }

      try {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL || process.env.API_URL
        const response = await fetch(`${apiUrl}/api/public/member/otp/${originalPhoneNumber}`, {
          method: "POST",
          body: JSON.stringify(requestBody),
          headers: { "Content-Type": "application/json" },
        })

        if (response.ok) {
          console.log("WhatsApp OTP request successful!")
          return true
        } else {
          const errorData = await response.json().catch(() => ({}))
          console.error("Failed to send WhatsApp OTP:", response.status, errorData)
          flash("error", errorData.message || "Failed to send WhatsApp OTP.")
          return false
        }
      } catch (error) {
        console.error("Error sending WhatsApp OTP request:", error)
        flash("error", "Could not request WhatsApp OTP. Please try again.")
        return false
      }
    },
    [flash],
  )

  const onSubmit = async (data) => {
    const formattedDob = data?.dob
      ? data?.dob.toISOString().replace("T", " ").substring(0, 23)
      : null

    const phoneNum = countryCode + data?.phoneNumber
    const phoneNumLast = phoneNum.replace(/\s/g, "")

    const formattedPhone = phoneNumLast.startsWith("+") ? phoneNumLast?.slice(1) : phoneNumLast

    setIsLoading(true)

    if (inactiveUser) {
      const existingUserPayload = {
        contact_number: formattedPhone,
        email: data?.email || null,
        name: data?.name,
        race: data?.race.toString(),
        company_code: process.env.NEXT_PUBLIC_COMPANY_CODE || process.env.COMPANY_CODE,
        dob: formattedDob,
        pwd: data?.pin,
        member_id: data?.memberId,
        gender: data?.gender,
        marital_status: data?.maritalStatus,
        confirmPwd: data?.confirmPin,
      }

      const otpSuccess = await handleWhatsAppPress({
        ...existingUserPayload,
        contact_number: formattedPhone,
      })
      setIsLoading(false)

      if (otpSuccess) {
        navigation.navigate("verification", {
          routeFrom: "register",
          inactiveUser: true,
          phoneNumber: formattedPhone,
          userData: existingUserPayload,
        })
        setTimeout(() => {
          flash("success", `OTP has been sent to ${formattedPhone} (WhatsApp)!`)
        }, 500)
      } else {
        console.log("Failed to send otp to existing user.")
      }
    } else {
      if (userData?.status === "unverified") {
        const userData = {
          contact_number: formattedPhone,
          pwd: data?.confirmPin,
          email: data?.email,
          name: data?.name,
          race: data?.race || null,
          status: "active",
          company_code: process.env.COMPANY_CODE,
          dob: formattedDob,
          gender: data?.gender || null,
          marital_status: data?.maritalStatus || null,
          referral_code_used: data?.referral_code_used || null,
        }

        navigation.navigate("verification", {
          routeFrom: "register",
          phoneNumber: formattedPhone,
          pwd: data?.pin,
          existUser,
          userData,
        })
        await fetch(`${process.env.API_URL}/api/client/otp/${formattedPhone}`, {
          method: "POST",
          body: JSON.stringify({
            configure_name: "whatsapp",
            company_code: process.env.COMPANY_CODE,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        })
      } else {
        await fetch(`${process.env.API_URL}/api/client/register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contact_number: formattedPhone,
            pwd: data?.pin,
            // identity_number: data?.ic,
            email: data?.email,
            identity_type: data?.idType,
            name: data?.name,
            outlet_code: outletCode || null,
            // dob: moment(formattedBirthDate, "DD-MM-YYYY").toISOString() || null,
            gender: data?.gender || null,
            race: data?.race || null,
            status: "active",
            company_code: process.env.COMPANY_CODE,
            dob: formattedDob,
            marital_status: data?.maritalStatus || null,
            referral_code_used: data?.referral_code_used || null,
          }),
        })
          .then(async (res) => {
            setIsLoading(false)
            if (res?.ok) {
              flash("success", translate("registerScreen.registerSuccess"))

              const constructedFlagInfo = selectedFullCountryInfo
                ? {
                    callingCode: selectedFullCountryInfo.callingCode,
                    cca2: selectedFullCountryInfo.cca2,
                    flag: selectedFullCountryInfo.flag,
                  }
                : {
                    callingCode: "+60",
                    cca2: "MY",
                    flag: "MY",
                  }

              const userData = {
                contact_number: formattedPhone,
                pwd: data?.pin,
                // identity_number: data?.ic,
                email: data?.email,
                identity_type: data?.idType,
                name: data?.name,
                // dob: moment(formattedBirthDate, "DD-MM-YYYY").toISOString() || null,
                gender: data?.gender || null,
                race: data?.race || null,
                status: "active",
                company_code: process.env.COMPANY_CODE,
                dob: formattedDob,
                marital_status: data?.maritalStatus,
                flagInfo: constructedFlagInfo,
                referral_code_used: data?.referral_code_used || null,
              }

              navigation.navigate("verification", {
                routeFrom: "register",
                phoneNumber: formattedPhone,
                pwd: data?.pin,
                existUser,
                userData,
              })
              await fetch(`${process.env.API_URL}/api/client/otp/${formattedPhone}`, {
                method: "POST",
                body: JSON.stringify({
                  configure_name: "whatsapp",
                  company_code: process.env.COMPANY_CODE,
                }),
                headers: {
                  "Content-Type": "application/json",
                },
              })
            } else {
              const response = await res?.json()

              console.log(response, "---response")
              if (response?.error === "MOBILENO_EXIST") {
                flash("error", translate("registerScreen.registerFailedNumberExist"))
              } else {
                flash("error", translate("registerScreen.registerFailed"))
              }
            }
          })
          .catch((error) => {
            setIsLoading(false)
            console.error("Error:", error)
          })
      }
    }
  }

  const getUserData = async () => {
    const phoneNum = countryCode + watch("phoneNumber") || ""
    const phoneNumLast = phoneNum.replace(/\s/g, "")

    const formattedPhone = phoneNumLast.startsWith("+") ? phoneNumLast?.slice(1) : phoneNumLast

    await fetch(
      `${process.env.API_URL}/api/public/check/${formattedPhone}/${process.env.COMPANY_CODE}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
      .then(async (res) => {
        if (res?.ok) {
          const responseData = await res?.json()
          setValue("name", responseData[0]?.name)
          setValue("email", responseData[0]?.email)
          setExistUser(true)
          setUserData(responseData[0])
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }

  return (
    <KeyboardAvoidingView
      {...(Platform.OS !== "web" && {
        behavior: Platform.OS === "ios" ? "padding" : "height",
        keyboardVerticalOffset: StatusBar.currentHeight + 10,
      })}
      style={{ flex: 1 }}
    >
      <Screen preset="scroll" style={[style.flex, { marginTop: StatusBar.currentHeight + 10 }]}>
        <View style={style.container}>
          <View style={{ flexDirection: "row", alignItems: "center", marginTop: 5 }}>
            <TouchableOpacity
              style={{
                backgroundColor: companyTheme.first,
                borderRadius: 8,
                paddingHorizontal: 25,
                paddingVertical: 5,
              }}
              onPress={() => navigation.navigate("login")}
            >
              <Icon name="chevron-left" size={25} color="white" />
            </TouchableOpacity>
          </View>

          {inactiveUser ? (
            <View style={style.mv30}>
              <Text style={style.titleText}>{translate("existingRegisterScreen.updateExist")}</Text>
              <Text style={{ fontFamily: typography.primary.normal }}>
                {translate("existingRegisterScreen.description")}
              </Text>
            </View>
          ) : (
            <View style={style.mv30}>
              <Text style={style.titleText}>{translate("registerScreen.signUp")}</Text>
              <Text style={{ fontFamily: typography.primary.normal }}>
                {translate("registerScreen.description")}
              </Text>
            </View>
          )}

          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: translate("errors.required"),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <PhoneNumberInput
                  value={value || ""}
                  editable={!inactiveUser || !initialUserData?.contact_number}
                  defaultCountry={initialDefaultCountry}
                  onChange={(formattedPhoneNumber) => {
                    onChange(formattedPhoneNumber)
                  }}
                  onBlur={() => getUserData()}
                  onCustomFocus={() => clearErrors("phoneNumber")}
                  fieldError={error}
                  errorMessage={error?.message}
                  placeholder={translate("loginScreen.phoneNumberPlaceholder")}
                  title={
                    <Text>
                      {translate("loginScreen.phoneNumber")}
                      <Text style={{ color: "red" }}></Text>
                    </Text>
                  }
                  containerStyle={style.inputContainer}
                  inputStyle={style.inputStyle}
                  onCountryChange={(country) => {
                    if (country) {
                      setCountryCode(country.callingCode)
                      setInitialDefaultCountry(country.cca2)
                      setSelectedFullCountryInfo(country) // <--- 更新这个 state
                    } else {
                      setCountryCode("")
                      setInitialDefaultCountry(null)
                      setSelectedFullCountryInfo(null) // <--- 更新这个 state
                    }
                  }}
                />
              )
            }}
          />
          <Controller
            name="name"
            control={control}
            rules={{
              required: translate("errors.required"),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <TextInput
                    onFocus={() => clearErrors("name")}
                    value={value}
                    onChangeText={onChange}
                    error={error}
                    // title={translate("registerScreen.name")}
                    title={<Text>{translate("registerScreen.fullName")}</Text>}
                    errorMessage={error?.message}
                    placeholder={translate("registerScreen.fullNamePlaceholder")}
                    containerStyle={style.inputContainer}
                    style={style.inputStyle}
                  />
                </View>
              )
            }}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: translate("errors.required"),
              pattern: {
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: translate("errors.invalidEmail"),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <>
                  <TextInput
                    error={error}
                    errorMessage={error?.message}
                    title={<Text>{translate("editProfileScreen.email")}</Text>}
                    value={value}
                    onChangeText={onChange}
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    placeholder={translate("editProfileScreen.emailPlaceholder")}
                  />
                </>
              )
            }}
          />

          <Controller
            name="dob"
            control={control}
            rules={
              {
                // required: translate("errors.required"),
              }
            }
            render={({ field: { value }, fieldState: { error } }) => {
              return (
                <>
                  <TextInput
                    error={error}
                    errorMessage={error?.message}
                    value={value && moment(value).format("DD-MM-YYYY")}
                    onPress={!inactiveUser && showDatePicker}
                    // title={translate("registerScreen.dob")}
                    title={
                      <Text>
                        DOB
                        {/* <Text style={{ color: "red" }}>*</Text> */}
                      </Text>
                    }
                    editable={false}
                    selectTextOnFocus={false}
                    placeholder={translate("editProfileScreen.dobPlaceholder")}
                    textFieldContainer={error && style.errorCell}
                  />
                  <DatePickerModal
                    closeIcon={() => null}
                    allowEditing={false}
                    locale={"en"}
                    date={value}
                    mode="single"
                    visible={isDatePickerVisible}
                    onDismiss={hideDatePicker}
                    onConfirm={(e) => {
                      setValue("dob", e?.date)
                      hideDatePicker()
                    }}
                    endYear={new Date().getFullYear()}
                    inputEnabled={false}
                    saveLabel={"Done"}
                    uppercase
                    label=" "
                    validRange={{ endDate: new Date() }}
                  />
                </>
              )
            }}
          />

          {/* Race */}
          <View style={[style.idContainer, { zIndex: 10000 }]}>
            <Controller
              name="race"
              control={control}
              rules={
                {
                  // required: translate("errors.required"),
                }
              }
              defaultValue={"malay"}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <View style={{ position: "relative", zIndex: 10000, flex: 1 }}>
                    <View style={style.idTypeContainer}>
                      <View style={style.titleContainer}>
                        <Text style={{ fontFamily: typography.primary.normal, fontSize: 12 }}>
                          {translate("registerScreen.race")}
                          {/* <Text style={{ color: "red" }}>*</Text> */}
                        </Text>
                      </View>
                      <DropDownPicker
                        style={[style.dropdown, error && style.errorCell]}
                        dropDownContainerStyle={{
                          borderColor: colors.inputFieldBorder,
                          backgroundColor: "white",
                          position: "absolute",
                          zIndex: 10000,
                        }}
                        placeholderStyle={{
                          color: colors.placeholder,
                        }}
                        open={idVisible}
                        listMode="SCROLLVIEW"
                        dropDownDirection="BOTTOM"
                        value={value}
                        items={raceList}
                        setOpen={() => {
                          setIDVisible(!idVisible)
                        }}
                        // setValue={onChange}
                        onSelectItem={(e) => onChange(e?.value)}
                      />
                      {error && (
                        <ErrorMessage errorType={error.type} customMessage={error?.message} />
                      )}
                    </View>
                  </View>
                )
              }}
            />
          </View>

          {/* Gender */}
          <View style={[style.idContainer, { zIndex: 999 }]}>
            <Controller
              name="gender"
              control={control}
              rules={
                {
                  // required: translate("errors.required"),
                }
              }
              defaultValue={"male"}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <View style={{ position: "relative", zIndex: 10000, flex: 1 }}>
                    <View style={style.idTypeContainer}>
                      <View style={style.titleContainer}>
                        <Text style={{ fontFamily: typography.primary.normal, fontSize: 12 }}>
                          {translate("editProfileScreen.gender")}
                        </Text>
                      </View>
                      <DropDownPicker
                        style={[style.dropdown, error && style.errorCell]}
                        dropDownContainerStyle={{
                          borderColor: colors.inputFieldBorder,
                          backgroundColor: "white",
                          position: "absolute",
                          zIndex: 10000,
                        }}
                        placeholderStyle={{
                          color: colors.placeholder,
                        }}
                        open={genderVisible}
                        listMode="SCROLLVIEW"
                        dropDownDirection="BOTTOM"
                        value={value}
                        items={genderList}
                        setOpen={() => {
                          setGenderVisible(!genderVisible)
                        }}
                        // setValue={onChange}
                        onSelectItem={(e) => onChange(e?.value)}
                      />
                      {error && (
                        <ErrorMessage errorType={error.type} customMessage={error?.message} />
                      )}
                    </View>
                  </View>
                )
              }}
            />
          </View>

          {/* Marital Status */}
          {/* <View style={[style.idContainer, { zIndex: 777 }]}>
            <Controller
              name="maritalStatus"
              control={control}
              rules={
                {
                  // required: translate("errors.required"),
                }
              }
              defaultValue={"single"}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <View style={{ position: "relative", zIndex: 10000, flex: 1 }}>
                    <View style={style.idTypeContainer}>
                      <View style={style.titleContainer}>
                        <Text style={{ fontFamily: typography.primary.normal, fontSize: 12 }}>
                          {translate("registerScreen.maritalStatus")}
                        </Text>
                      </View>
                      <DropDownPicker
                        style={[style.dropdown, error && style.errorCell]}
                        dropDownContainerStyle={{
                          borderColor: colors.inputFieldBorder,
                          backgroundColor: "white",
                          position: "absolute",
                          zIndex: 10000,
                        }}
                        placeholderStyle={{
                          color: colors.placeholder,
                        }}
                        open={marriedVisible}
                        listMode="SCROLLVIEW"
                        dropDownDirection="BOTTOM"
                        value={value}
                        items={maritalList}
                        setOpen={() => {
                          setMarriedVisible(!marriedVisible)
                        }}
                        // setValue={onChange}
                        onSelectItem={(e) => onChange(e?.value)}
                      />
                      {error && (
                        <ErrorMessage errorType={error.type} customMessage={error?.message} />
                      )}
                    </View>
                  </View>
                )
              }}
            />
          </View> */}

          {/* Referral */}
          {/* <Controller
            name="referral_code_used"
            defaultValue={referralCode}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              if (inactiveUser) {
                return null
              }

              return (
                <>
                  <TextInput
                    onFocus={() => clearErrors("referral_code_used")}
                    keyboardType="default"
                    value={value}
                    onChangeText={(text) => {
                      onChange(text)
                    }}
                    error={error}
                    title={<Text>{translate("registerScreen.referralCode")}</Text>}
                    errorMessage={error?.message}
                    placeholder={"Please enter" + " " + translate("registerScreen.referral")}
                    containerStyle={style.inputContainer}
                    style={style.inputStyle}
                  />
                </>
              )
            }}
          /> */}

          {/* Pin */}
          <Controller
            name="pin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>
                    {translate("common.pin")}
                    <Text style={{ color: "red" }}>*</Text>
                  </Text>
                  <CodeField
                    ref={pinRef}
                    {...pinProps}
                    onFocus={() => {
                      clearErrors("pin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("pin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.mb10}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                        ]}
                        onLayout={getPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />

          {/* Confirm Pin */}
          <Controller
            name="confirmPin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
              validate: {
                value: (value) => watch("pin") === value || translate("errors.samePin"),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>
                    {translate("common.confirmPin")}
                    <Text style={{ color: "red" }}>*</Text>
                  </Text>
                  <CodeField
                    ref={confirmPinRef}
                    {...confirmPinProps}
                    onFocus={() => {
                      clearErrors("confirmPin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("confirmPin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.mb10}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                        ]}
                        onLayout={getConfirmPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />
          <View style={style.tncContainer}>
            <RadioButton
              style={{ width: 40, height: 40, alignItems: "center", justifyContent: "center" }}
              checked={isTncChecked}
              onPress={() => setIsTncChecked(!isTncChecked)}
            />
            <Text style={style.tncText}>
              {translate("registerScreen.iHaveAccept")}{" "}
              <Text
                onPress={
                  () => navigation.navigate("webView", { name: "Tnc" })
                  // TODO: show the tnc
                }
                style={{ color: companyTheme.first, fontFamily: typography.primary.normal }}
              >
                {translate("registerScreen.tnc")}
              </Text>{" "}
              {translate("common.and")}{" "}
              <Text
                onPress={
                  () => navigation.navigate("webView", { name: "PrivacyPolicy" })
                  // TODO: show the tnc
                }
                style={{ color: companyTheme.first, fontFamily: typography.primary.normal }}
              >
                {translate("registerScreen.privacyPolicy")}
              </Text>
            </Text>
          </View>

          <View style={style.buttonContainer}>
            <Button
              loading={isLoading}
              disabled={!isTncChecked}
              onPress={handleSubmit(onSubmit)}
              title={
                inactiveUser
                  ? translate("existingRegisterScreen.update")
                  : translate("common.register")
              }
              style={!isTncChecked && style.disabledButton}
            />
          </View>
        </View>
      </Screen>
    </KeyboardAvoidingView>
  )
})
